import { FormUnit } from '@mi-front/ui';
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import { useFormikContext } from 'formik';

interface StyledAutocompleteFieldProps<T>
  extends Omit<AutocompleteProps<T, true, false, false>, 'renderInput'> {
  name: string;
  label: string;
  placeholder: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  /** When true, input is not editable but not visually disabled (readonly). */
  readOnly?: boolean;
}

export function StyledAutocompleteField<T>({
  name,
  label,
  placeholder,
  helperText,
  error = false,
  errorMessage,
  readOnly,
  ...autocompleteProps
}: StyledAutocompleteFieldProps<T>) {
  const { errors, touched } = useFormikContext();
  const hasError = touched[name as keyof typeof touched] && !!errors[name as keyof typeof errors];
  const displayError = error || hasError;
  const displayErrorMessage =
    errorMessage ||
    (hasError && typeof errors[name as keyof typeof errors] === 'string'
      ? (errors[name as keyof typeof errors] as string)
      : undefined);

  return (
    <FormUnit label={label} error={displayErrorMessage}>
      <Autocomplete
        {...autocompleteProps}
        openOnFocus={readOnly ? false : autocompleteProps.openOnFocus}
        open={readOnly ? false : autocompleteProps.open}
        onOpen={readOnly ? undefined : autocompleteProps.onOpen}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            helperText={helperText}
            error={displayError}
            inputProps={{ ...params.inputProps, readOnly: readOnly ?? false }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: '38px',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                '& fieldset': {
                  borderColor: displayError ? '#dc3545' : '#ced4da',
                  borderRadius: '0.375rem',
                },
                '&:hover fieldset': {
                  borderColor: displayError ? '#dc3545' : '#ced4da',
                },
                '&.Mui-focused fieldset': {
                  borderColor: displayError ? '#dc3545' : '#9ae3fe',
                  boxShadow: displayError
                    ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
                    : '0 0 0 0.2rem rgba(28, 192, 252, 0.25)',
                },
                '&.Mui-disabled fieldset': {
                  borderColor: '#ced4da',
                  backgroundColor: '#e9ecef',
                },
              },
              '& .MuiInputBase-root': {
                padding: '0rem 0.25rem',
              },
              '& .MuiInputBase-input': {
                color: '#343a40',
                backgroundColor: '#ffffff',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#808080',
                opacity: 1,
              },
            }}
          />
        )}
        sx={{
          '& .MuiAutocomplete-endAdornment': {
            right: '8px',
            '& .MuiSvgIcon-root': {
              color: '#ccc',
            },
          },
          '& .MuiAutocomplete-root': {
            backgroundColor: '#ffffff',
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
          },
        }}
      />
    </FormUnit>
  );
}
